import { Close, CloudUpload } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Box, styled, useTheme } from "@mui/system";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { ChangeEvent, useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";

const ACCEPT = {
  excel: {
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
  },
  all: {
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
    "application/vnd.ms-powerpoint": [".ppt"],
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      [".pptx"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      ".docx",
    ],
    "application/pdf": [".pdf"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "text/plain": [".txt"],
    "text/markdown": [".md"],
  },
};

const SUPPORTED_FILE_TYPES = {
  excel: "Supported file types: .xls, .xlsx",
  all: "Supported file types: .xls(x), .ppt(x), .doc(x), .pdf, .jp(e)g, .png, .txt, .md",
};

export type UploadDialogProps = {
  isOpen: boolean;
  hideCommentField?: boolean;
  fileType: "all" | "excel";
};

export type UploadDialogEvents =
  | "upload-dialog:close"
  | "upload-dialog:upload-file";

export type UploadDialogEventPayload = {
  file: File;
  comment: string;
};

export type UploadDialogFireEventType =
  | ((event: UploadDialogEvents, payload?: UploadDialogEventPayload) => void)
  | undefined;

export const UploadDialog: PimoReactComponent<
  UploadDialogProps,
  UploadDialogEvents,
  UploadDialogEventPayload
> = ({ isOpen, fireEvent, fileType, hideCommentField = false }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [comment, setComment] = useState("");
  const onCommentChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;

    if (value.length > 160) {
      return;
    }

    setComment(event.target.value);
  };
  const handleClose = () => fireEvent?.("upload-dialog:close");
  const handleUpload = () => {
    if (selectedFile) {
      fireEvent?.("upload-dialog:upload-file", {
        file: selectedFile,
        comment: comment,
      });

      setSelectedFile(null);
      setComment("");
    }
  };
  const onDrop = useCallback((droppedFiles: File[]) => {
    if (droppedFiles) {
      setSelectedFile(droppedFiles[0]);
    }
  }, []);
  const { getRootProps, getInputProps, inputRef, isDragActive } = useDropzone({
    multiple: false,
    accept: ACCEPT[fileType],
    onDrop,
  });

  return (
    <Dialog
      sx={{
        margin: "50px",
        zIndex: 9999,
      }}
      PaperProps={{ sx: { borderRadius: "12px" } }}
      maxWidth="md"
      open={isOpen}
      onClose={handleClose}
      scroll="paper"
    >
      <DialogHeader>
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "22px",
          }}
        >
          Document Upload
        </DialogTitle>
        <Box marginY="auto">
          <IconButton aria-label="close" onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogHeader>
      <DialogContent sx={{ padding: 0, borderBottom: "none" }} dividers>
        <ContentBox
          onClick={() => {
            if (inputRef.current) {
              inputRef.current.click();
            }
          }}
          {...getRootProps()}
        >
          <DropZoneBox
            selectedFile={selectedFile}
            isDragActive={isDragActive}
            supportedFileTypes={SUPPORTED_FILE_TYPES[fileType]}
          />
          <input
            type="file"
            name="supportingDocumentsUpload"
            onChange={(event) => {
              const files = event.currentTarget.files;

              if (files) {
                setSelectedFile(Array.from(files)[0]);
              }
            }}
            {...getInputProps()}
          />
        </ContentBox>
        {!hideCommentField && (
          <CommentBox>
            <Typography>
              <b>Comment</b> (optional, restricted to 160 characters)
            </Typography>
            <TextField
              sx={{
                fieldset: {
                  borderRadius: "8px",
                },
              }}
              multiline
              fullWidth
              value={comment}
              onChange={onCommentChange}
            />
          </CommentBox>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          margin: "32px",
        }}
      >
        <Button
          sx={(theme) => ({
            borderRadius: "4px",
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          })}
          onClick={handleClose}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ borderRadius: "4px", color: "#FFF" }}
          onClick={handleUpload}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DialogHeader = styled(Box)(({ theme }) => ({
  marginLeft: "40px",
  display: "flex",
  justifyContent: "space-between",
  paddingRight: theme.spacing(2),
}));

function DropZoneBox({
  selectedFile,
  isDragActive,
  supportedFileTypes,
}: {
  selectedFile: File | null;
  isDragActive: boolean;
  supportedFileTypes: string;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
      }}
    >
      {selectedFile && (
        <Typography key={selectedFile.name}>
          Selected File: {selectedFile.name}
        </Typography>
      )}
      <CloudUpload
        sx={{
          color: theme.palette.primary.main,
          width: "53px",
          height: "53px",
        }}
      />
      <Typography sx={{ marginBottom: "8px" }}>
        {isDragActive
          ? "Drop the files here..."
          : "Drag & drop file or select from computer"}
      </Typography>
      <Typography color={theme.palette.grey[700]}>
        {supportedFileTypes}
      </Typography>
    </Box>
  );
}

const ContentBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: "8px",
  alignItems: "center",
  width: "656px",
  minHeight: "143px",
  border: "1px dotted blue",
  borderRadius: "8px",
  margin: "32px",
  padding: "8px",
  textTransform: "unset",
  cursor: "pointer",
});
const CommentBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  borderRadius: "8px",
  margin: "32px",
});
